import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { API_URL, getAccessToken } from "./api";
import { useAuth } from "./auth";

export interface LiveNotification {
  id: string;
  type: string;
  title: string;
  body: string;
}

type Listener = (n: LiveNotification) => void;
const listeners = new Set<Listener>();

/** Subscribe to live notifications (e.g. to show a toast). Returns an unsubscribe. */
export function onLiveNotification(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Maintains a WebSocket to the API while authenticated. On each pushed
 * notification it refreshes the notifications query and fans out to listeners.
 * Reconnects with backoff; tears down on logout.
 */
export function useLiveNotifications(): void {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    let socket: WebSocket | null = null;
    let closed = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout>;

    const connect = (): void => {
      const token = getAccessToken();
      if (!token) return;
      const wsUrl = `${API_URL.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token)}`;
      socket = new WebSocket(wsUrl);

      socket.onmessage = (event: MessageEvent<string>) => {
        try {
          const msg = JSON.parse(event.data) as {
            kind: string;
            notification?: LiveNotification;
          };
          if (msg.kind === "notification" && msg.notification) {
            void qc.invalidateQueries({ queryKey: ["notifications"] });
            void qc.invalidateQueries({ queryKey: ["accounts"] });
            void qc.invalidateQueries({ queryKey: ["transactions"] });
            for (const fn of listeners) fn(msg.notification);
          }
        } catch {
          // ignore malformed frames
        }
      };

      socket.onclose = () => {
        if (closed) return;
        retry = Math.min(retry + 1, 6);
        timer = setTimeout(connect, 1000 * 2 ** retry);
      };
      socket.onopen = () => {
        retry = 0;
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      socket?.close();
    };
  }, [user, qc]);
}
